/**
 * Time Machine #114 Phase 5 — pure request-body builder for the Lambda's
 * `getEventLogByDocumentId` method. Drives the History section of
 * `<TimeMachineJobModal>` in the frontend.
 *
 * Inputs:
 *   targetCollection — Attributes / ActiveQueue / Candidates
 *   documentId       — ObjectId hex string of the document whose history
 *                      to fetch (24-char hex: 0-9, a-f)
 *   database         — "Warehouses" or "UAT". NOTE (#121): Lambda
 *                      `v05-09-2026-04-QRY01`+ overrides to "Warehouses"
 *                      for all Time Machine read methods regardless of
 *                      value passed.
 *   since (optional) — ISO timestamp lower bound (inclusive)
 *   until (optional) — ISO timestamp upper bound (inclusive)
 *   limit (optional) — int, default 200, max 1000
 */

const VALID_TARGET_COLLECTIONS = ["Attributes", "ActiveQueue", "Candidates"];
const VALID_DATABASES = ["Warehouses", "UAT"];
const OBJECTID_HEX_RE = /^[0-9a-fA-F]{24}$/;

export function buildEventLogByDocumentIdRequestBody({
  targetCollection,
  documentId,
  database,
  since,
  until,
  limit,
} = {}) {
  if (!VALID_TARGET_COLLECTIONS.includes(targetCollection)) {
    throw new Error(
      `buildEventLogByDocumentIdRequestBody: targetCollection must be one of ${VALID_TARGET_COLLECTIONS.join(", ")} (got ${JSON.stringify(targetCollection)})`
    );
  }
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildEventLogByDocumentIdRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }
  if (typeof documentId !== "string" || !OBJECTID_HEX_RE.test(documentId)) {
    throw new Error(
      `buildEventLogByDocumentIdRequestBody: documentId must be a 24-char hex string (got ${JSON.stringify(documentId)})`
    );
  }

  const body = {
    method: "getEventLogByDocumentId",
    database,
    collection: "AuditSnapshots", // dispatcher artifact
    targetCollection,
    documentId,
  };

  if (since != null) {
    const sinceIso = toIsoOrThrow(since, "since");
    body.since = sinceIso;
  }
  if (until != null) {
    const untilIso = toIsoOrThrow(until, "until");
    body.until = untilIso;
  }
  if (limit != null) {
    if (typeof limit !== "number" || !Number.isFinite(limit) || limit <= 0) {
      throw new Error(
        `buildEventLogByDocumentIdRequestBody: limit must be a positive finite number (got ${JSON.stringify(limit)})`
      );
    }
    body.limit = Math.min(Math.floor(limit), 1000);
  }

  return body;
}

function toIsoOrThrow(value, label) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    throw new Error(
      `buildEventLogByDocumentIdRequestBody: ${label} must be a Date or ISO string (got ${JSON.stringify(value)})`
    );
  }
  return d.toISOString();
}

export const __testables__ = {
  VALID_TARGET_COLLECTIONS,
  VALID_DATABASES,
  OBJECTID_HEX_RE,
};
